import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useGetDatasetByIdQuery } from "@store/api/dataset-api";
import type { DatasetMetric } from "@models/Metric";

interface MetricBaseSummaryCellProps {
  datasetId: string;
  base: DatasetMetric["base"];
}

const MetricBaseSummaryCell: React.FC<MetricBaseSummaryCellProps> = ({ datasetId, base }) => {
  const isPreconfigured = base.mode === "preconfigured";
  const { data: dataset, isLoading, isError } = useGetDatasetByIdQuery(datasetId, {
    skip: !datasetId || !isPreconfigured,
  });

  const label =
    base.mode === "all_eligible"
      ? "All eligible respondents"
      : base.mode === "custom"
        ? base.name || "Custom base"
        : base.preconfiguredBaseName;

  // Confirmed invalid only once the dataset's bases resolve successfully and
  // don't include this reference — never flag on a transient load error.
  const isConfirmedInvalid =
    isPreconfigured &&
    !isLoading &&
    !isError &&
    !!dataset &&
    !(dataset.bases ?? []).some((b) => b.id === base.preconfiguredBaseId);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {isConfirmedInvalid && (
        <Tooltip title="This base is no longer available. Open the metric to repair it.">
          <WarningAmberIcon fontSize="small" color="warning" />
        </Tooltip>
      )}
      <Typography variant="body2" sx={{ whiteSpace: "normal", maxWidth: 240 }}>
        {label}
      </Typography>
    </Box>
  );
};

export default MetricBaseSummaryCell;
